import React, { useEffect, useCallback } from "react";

const CustomPagination = ({
  totalItems,
  itemsPerPage,
  maxPageNumbersToShow,
  currentPage = 1,
  setCurrentPage,
  storageKey = 'defaultPaginationPage'
}) => {
  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Debounce function to limit the rate of page change
  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  // Modify the useEffect to only set the page on initial mount
  useEffect(() => {
    const savedPage = localStorage.getItem(storageKey);
    if (savedPage && setCurrentPage && !currentPage) {  // Only set if currentPage is not already set
      const parsedPage = Number(savedPage);
      // Validate the saved page is within bounds
      if (parsedPage > 0 && parsedPage <= Math.ceil(totalItems / itemsPerPage)) {
        setCurrentPage(parsedPage);
      }
    }
  }, []); // Run only on mount

  // Modify the debounced handler to only save to localStorage
  const debouncedHandlePageChange = useCallback(
    debounce((pageNumber) => {
      if (pageNumber) {
        localStorage.setItem(storageKey, pageNumber.toString());
        setCurrentPage(pageNumber);
      }
    }, 300),
    [setCurrentPage, storageKey]
  );

  const generatePageNumbers = () => {
    const pageNumbers = [];

    if (totalPages <= maxPageNumbersToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const leftBound = Math.max(2, currentPage - 1);
      const rightBound = Math.min(totalPages - 1, currentPage + 1);

      pageNumbers.push(1);

      if (leftBound > 2) {
        pageNumbers.push("...");
      }

      for (let i = leftBound; i <= rightBound; i++) {
        pageNumbers.push(i);
      }

      if (rightBound < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbersToShow = generatePageNumbers();

  return (
    <div className="font-poppins  p-4">
      <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
        {/* Previous Button */}
        <button
          onClick={() =>
            debouncedHandlePageChange(
              currentPage && currentPage > 1 ? currentPage - 1 : 1
            )
          }
          disabled={currentPage === 1}
          className={`px-4 py-2 border border-gray-300 rounded-md text-xs font-normal flex items-center justify-center ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "hover:bg-gray-200 text-black"
          }`}
        >
          Previous
        </button>

        {/* Page Info */}
        <p className="text-sm text-black font-normal">
          Page {currentPage} of {totalPages}
        </p>

        {/* Next Button */}
        <button
          onClick={() =>
            debouncedHandlePageChange(
              currentPage && currentPage < totalPages
                ? currentPage + 1
                : totalPages
            )
          }
          disabled={currentPage === totalPages}
          className={`px-4 py-2 border border-gray-300 rounded-md text-xs font-normal flex items-center justify-center ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "hover:bg-gray-200 text-black"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;
